import { FontAwesomeIcons } from "../../../../core/Types";
import moment from "moment";
import Job from "../models/Job";

/*
 * The jobs board's advanced filters. Everything runs on the client: the board already holds
 * every active job, so a filter is just a predicate over that list.
 *
 * Each filter is a multi-select. Inside one filter the picked values are OR-ed ("Status is
 * Pending or Hold"); across filters they are AND-ed ("... and the tech hasn't called yet").
 * A filter's picked ids ride in the address under its `key`, comma separated, like the board's
 * other state.
 *
 * The date filters also offer "Custom range": a from/to date-time picked in the panel. The range
 * rides beside the filter as `<key>Range=<fromMs>~<toMs>` (either side may be empty = open end).
 * Everything is compared as UTC epoch ms: each job carries its dates as UTC numbers (filterTimes,
 * added once as it arrives from the server), and the picked "Nov 3 10:00" is read as 10:00 UTC.
 * No time zone takes part on either side.
 */

export interface JobFilterOption {
    id: string
    name: string
    color?: string
    icon?: FontAwesomeIcons
    count?: number
    /** A person's option: drawn with their avatar (or initial when avatarUrl is empty). */
    person?: boolean
    avatarUrl?: string
}

/** What the options and predicates need besides the jobs. */
export interface JobFilterContext {
    /** Whether the account has proxy numbers at all; without them a tech's call is never tracked. */
    maskedAllowed: boolean
    /** Status order for the Status options. */
    statusOrder: string[]
    /** Named job colours (CRM > Base Data > Job Colors). */
    jobColors: { name?: string, color?: string }[]
    /** The account's metros, for their colours. */
    metros: { id?: string, color?: string }[]
}

export interface JobFilterDef {
    key: string
    title: string
    icon: FontAwesomeIcons
    section: string
    options: (jobs: Job[], ctx: JobFilterContext) => JobFilterOption[]
    /** True when the job matches at least one of the picked values. */
    match: (job: Job, picked: string[], ctx: JobFilterContext) => boolean
    /** Date filters only: the job's date for the "Custom range" option. */
    dateOf?: (job: Job) => number | undefined
}

export type JobFilterValues = Record<string, string[]>

export const NONE = "__none"
export const CUSTOM = "custom"

/** Where a date filter's custom range is kept (in the values and in the address). */
export const rangeKey = (key: string) => `${key}Range`

export interface JobDateRange { from?: number, to?: number }

export const readRange = (values: JobFilterValues, key: string): JobDateRange | undefined => {
    const [from, to] = (values[rangeKey(key)]?.[0] ?? "").split("~").map(v => v ? Number(v) : undefined);
    if (!from && !to) return undefined;
    return { from: from || undefined, to: to || undefined };
}

export const writeRange = (range: JobDateRange | undefined): string[] | undefined =>
    range && (range.from || range.to) ? [`${range.from ?? ""}~${range.to ?? ""}`] : undefined;

const fmt = (t: number) => moment.utc(t).format("MMM D, h:mm A");
export const formatRange = (r: JobDateRange) =>
    r.from && r.to ? `${fmt(r.from)} – ${fmt(r.to)}` : r.from ? `After ${fmt(r.from)}` : `Before ${fmt(r.to!)}`;

const CUSTOM_OPTION: JobFilterOption = { id: CUSTOM, name: "Custom range...", icon: "calendar-range" };


// ---- helpers ---------------------------------------------------------------

/** Distinct values of one field across the jobs, with how many jobs carry each. */
type Picked = { id?: string, name?: string, color?: string, person?: boolean, avatarUrl?: string }

const distinct = (jobs: Job[], pick: (j: Job) => Picked | undefined,
    noneLabel?: string): JobFilterOption[] => {
    const map = new Map<string, JobFilterOption>();
    jobs.forEach(j => {
        const v = pick(j);
        const id = v?.id || NONE;
        if (id == NONE && !noneLabel) return;
        const existing = map.get(id);
        if (existing) existing.count = (existing.count ?? 0) + 1;
        else map.set(id, id == NONE
            ? { id, name: noneLabel!, count: 1 }
            : { id, name: v?.name || "-", color: v?.color, person: v?.person, avatarUrl: v?.avatarUrl, count: 1 });
    });
    // Most used first; the "none" bucket always last.
    return Array.from(map.values()).sort((a, b) =>
        a.id == NONE ? 1 : b.id == NONE ? -1 : (b.count ?? 0) - (a.count ?? 0));
}

/** Like distinct, for fields that hold several ids (flags, tags). */
const distinctMany = (jobs: Job[], pick: (j: Job) => { id?: string, name?: string, color?: string }[] | undefined,
    noneLabel: string): JobFilterOption[] => {
    const map = new Map<string, JobFilterOption>();
    let none = 0;
    jobs.forEach(j => {
        const list = pick(j) ?? [];
        if (list.length == 0) none++;
        list.forEach(v => {
            if (!v.id) return;
            const existing = map.get(v.id);
            if (existing) existing.count = (existing.count ?? 0) + 1;
            else map.set(v.id, { id: v.id, name: v.name ?? "-", color: v.color, count: 1 });
        });
    });
    const res = Array.from(map.values()).sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
    if (none > 0) res.push({ id: NONE, name: noneLabel, count: none });
    return res;
}

/** Fixed options (states rather than values), counted against the jobs, empty ones dropped. */
const states = (jobs: Job[], ctx: JobFilterContext, list: { id: string, name: string, icon?: FontAwesomeIcons, test: (j: Job, ctx: JobFilterContext) => boolean }[]) =>
    list
        .map(s => ({ id: s.id, name: s.name, icon: s.icon, count: jobs.filter(j => s.test(j, ctx)).length }))
        .filter(o => o.count > 0);

const testOf = (list: { id: string, test: (j: Job, ctx: JobFilterContext) => boolean }[]) =>
    (job: Job, picked: string[], ctx: JobFilterContext) =>
        list.some(s => picked.includes(s.id) && s.test(job, ctx));

const ms = (d?: Date | string) => d ? new Date(d).getTime() : undefined;

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

/** Start of today in the browser's time zone. */
const todayStart = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

const isOpen = (j: Job) => !j.isClosed && !j.isCanceled;
const completedCalls = (j: Job) => (j.jobCalls ?? []).filter(c => c.isCompleted);


// ---- state lists -------------------------------------------------------------

// Where the job is with its technician. Mirrors the board row's tech status line.
const TECH_PROGRESS = [
    { id: "unassigned", name: "No technician", icon: "user-slash" as FontAwesomeIcons, test: (j: Job) => !j.techId },
    { id: "notSent", name: "Assigned, not sent", icon: "paper-plane" as FontAwesomeIcons, test: (j: Job) => !!j.techId && !j.sentToTech },
    { id: "awaiting", name: "Sent, awaiting response", icon: "timer" as FontAwesomeIcons, test: (j: Job) => !!j.sentToTech && !j.techAccepted && isOpen(j) },
    { id: "accepted", name: "Accepted by tech", icon: "check-circle" as FontAwesomeIcons, test: (j: Job) => !!j.techAccepted },
    { id: "addedByTech", name: "Added by tech", icon: "user-helmet-safety" as FontAwesomeIcons, test: (j: Job) => !!j.techId && j.creatorUserId == j.techId },
]

// Has the technician phoned the customer? "Not called" is exactly the board's red/amber
// "Not Called!" case: accepted, no completed call, not added by the tech, not waived, and the
// account has proxy numbers (without them calls can't be tracked: "Direct Given").
const notCalled = (j: Job, ctx: JobFilterContext) =>
    !!j.tech && !!j.techAccepted && isOpen(j) && completedCalls(j).length == 0
    && j.creatorUserId != j.techId && !j.ignoreNotCalledAlert && ctx.maskedAllowed;
const notCalledFor = (minutes: number) => (j: Job, ctx: JobFilterContext) =>
    notCalled(j, ctx) && (Date.now() - (ms(j.techAcceptedAt) ?? Date.now())) > minutes * 60 * 1000;

const TECH_CALL = [
    { id: "called", name: "Tech called the customer", icon: "phone-circle" as FontAwesomeIcons, test: (j: Job) => completedCalls(j).length > 0 },
    { id: "notCalled", name: "Not called yet", icon: "phone-slash" as FontAwesomeIcons, test: notCalled },
    { id: "notCalled5", name: "Not called for 5+ min", icon: "phone-slash" as FontAwesomeIcons, test: notCalledFor(5) },
    { id: "notCalled30", name: "Not called for 30+ min", icon: "phone-slash" as FontAwesomeIcons, test: notCalledFor(30) },
    { id: "direct", name: "Direct number given", icon: "phone-arrow-up-right" as FontAwesomeIcons, test: (j: Job, ctx: JobFilterContext) => !!j.techAccepted && completedCalls(j).length == 0 && (!!j.ignoreNotCalledAlert || !ctx.maskedAllowed) },
]

const ACTIVITY = [
    { id: "hasCalls", name: "Has calls", icon: "phone" as FontAwesomeIcons, test: (j: Job) => (j.jobCalls?.length ?? 0) > 0 },
    { id: "noCalls", name: "No calls", icon: "phone-xmark" as FontAwesomeIcons, test: (j: Job) => (j.jobCalls?.length ?? 0) == 0 },
    { id: "newCalls", name: "Unheard calls", icon: "phone-volume" as FontAwesomeIcons, test: (j: Job) => completedCalls(j).some(c => (c.listeners?.length ?? 0) < 1) },
    { id: "unseenNotes", name: "Unread notes", icon: "note" as FontAwesomeIcons, test: (j: Job) => !!j.unseenNotes },
    { id: "hasNotes", name: "Has notes", icon: "note" as FontAwesomeIcons, test: (j: Job) => (j.notesCount ?? 0) > 0 },
    { id: "hasAttachments", name: "Has attachments", icon: "paperclip" as FontAwesomeIcons, test: (j: Job) => (j.attachmentsCount ?? 0) > 0 },
    { id: "hasTasks", name: "Has tasks", icon: "alarm-clock" as FontAwesomeIcons, test: (j: Job) => (j.tasksCount ?? 0) > 0 },
    { id: "hasEstimates", name: "Has estimates", icon: "file-invoice" as FontAwesomeIcons, test: (j: Job) => (j.estimatesCount ?? 0) > 0 },
    { id: "hasInvoices", name: "Has invoices", icon: "file-invoice-dollar" as FontAwesomeIcons, test: (j: Job) => (j.invoicesCount ?? 0) > 0 },
    { id: "hasDetails", name: "Has job details", icon: "memo" as FontAwesomeIcons, test: (j: Job) => !window.isNullOrEmpty(j.jobDetails) },
]

const appt = (j: Job) => ms(j.appointmentFrom);
const APPOINTMENT = [
    { id: "none", name: "No appointment", icon: "calendar-xmark" as FontAwesomeIcons, test: (j: Job) => !j.appointmentDate },
    { id: "set", name: "Has appointment", icon: "calendar-check" as FontAwesomeIcons, test: (j: Job) => !!j.appointmentDate },
    { id: "overdue", name: "Overdue (passed, not started)", icon: "calendar-exclamation" as FontAwesomeIcons, test: (j: Job) => !!j.appointmentDate && (appt(j) ?? Infinity) < Date.now() && isOpen(j) && !j.isInProgress && !j.startedAt },
    { id: "nextHour", name: "Within the next hour", icon: "clock" as FontAwesomeIcons, test: (j: Job) => { const t = appt(j); return !!j.appointmentDate && t != null && t >= Date.now() && t < Date.now() + HOUR } },
    { id: "today", name: "Today", icon: "calendar-day" as FontAwesomeIcons, test: (j: Job) => { const t = appt(j); return !!j.appointmentDate && t != null && t >= todayStart() && t < todayStart() + DAY } },
    { id: "tomorrow", name: "Tomorrow", icon: "calendar-day" as FontAwesomeIcons, test: (j: Job) => { const t = appt(j); return !!j.appointmentDate && t != null && t >= todayStart() + DAY && t < todayStart() + 2 * DAY } },
    { id: "week", name: "Next 7 days", icon: "calendar-week" as FontAwesomeIcons, test: (j: Job) => { const t = appt(j); return !!j.appointmentDate && t != null && t >= todayStart() && t < todayStart() + 7 * DAY } },
]

const CREATED = [
    { id: "today", name: "Today", icon: "calendar-plus" as FontAwesomeIcons, test: (j: Job) => (ms(j.createdAt) ?? 0) >= todayStart() },
    { id: "yesterday", name: "Yesterday", icon: "calendar-plus" as FontAwesomeIcons, test: (j: Job) => { const t = ms(j.createdAt) ?? 0; return t >= todayStart() - DAY && t < todayStart() } },
    { id: "week", name: "Last 7 days", icon: "calendar-plus" as FontAwesomeIcons, test: (j: Job) => (ms(j.createdAt) ?? 0) >= todayStart() - 6 * DAY },
    { id: "month", name: "Last 30 days", icon: "calendar-plus" as FontAwesomeIcons, test: (j: Job) => (ms(j.createdAt) ?? 0) >= todayStart() - 29 * DAY },
    { id: "older", name: "Older than 30 days", icon: "calendar" as FontAwesomeIcons, test: (j: Job) => (ms(j.createdAt) ?? 0) < todayStart() - 29 * DAY },
]

const modified = (j: Job) => ms(j.modifiedAt ?? j.createdAt) ?? 0;
const MODIFIED = [
    { id: "hour", name: "Last hour", icon: "clock" as FontAwesomeIcons, test: (j: Job) => modified(j) >= Date.now() - HOUR },
    { id: "today", name: "Today", icon: "calendar-pen" as FontAwesomeIcons, test: (j: Job) => modified(j) >= todayStart() },
    { id: "yesterday", name: "Yesterday", icon: "calendar-pen" as FontAwesomeIcons, test: (j: Job) => { const t = modified(j); return t >= todayStart() - DAY && t < todayStart() } },
    { id: "week", name: "Last 7 days", icon: "calendar-pen" as FontAwesomeIcons, test: (j: Job) => modified(j) >= todayStart() - 6 * DAY },
    { id: "notWeek", name: "Not in the last 7 days", icon: "calendar" as FontAwesomeIcons, test: (j: Job) => modified(j) < todayStart() - 6 * DAY },
    { id: "notMonth", name: "Not in the last 30 days", icon: "calendar" as FontAwesomeIcons, test: (j: Job) => modified(j) < todayStart() - 29 * DAY },
]

const DATA_QUALITY = [
    { id: "noAddress", name: "Missing address", icon: "location-dot" as FontAwesomeIcons, test: (j: Job) => !j.address?.title },
    { id: "noSource", name: "Missing source", icon: "bullhorn" as FontAwesomeIcons, test: (j: Job) => !j.jobSourceId },
    { id: "noMetro", name: "Missing metro", icon: "map" as FontAwesomeIcons, test: (j: Job) => !j.metroId },
    { id: "noTimezone", name: "Missing time zone", icon: "globe" as FontAwesomeIcons, test: (j: Job) => !(j.stateId && j.timezone) },
    { id: "hasCompany", name: "Has a company", icon: "briefcase" as FontAwesomeIcons, test: (j: Job) => !window.isNullOrEmpty(j.company) },
    { id: "hasContactPhone", name: "Has a second phone", icon: "phone-rotary" as FontAwesomeIcons, test: (j: Job) => !!j.phoneContact?.phoneFriendly },
]


// ---- the filters ---------------------------------------------------------------

export const JOB_FILTERS: JobFilterDef[] = [
    // Job
    {
        key: "statuses", title: "Status", icon: "bars-progress", section: "Job",
        options: (jobs, ctx) => distinct(jobs, j => ({ id: j.jobStatusId, name: j.jobStatus?.name, color: j.jobStatus?.color }))
            .sort((a, b) => {
                const rank = (id: string) => { const i = ctx.statusOrder.indexOf(id); return i < 0 ? Number.MAX_SAFE_INTEGER : i };
                return rank(a.id) - rank(b.id);
            }),
        match: (j, p) => p.includes(j.jobStatusId ?? NONE),
    },
    {
        key: "types", title: "Job Type", icon: "wrench", section: "Job",
        options: jobs => distinct(jobs, j => ({ id: j.jobTypeId, name: j.jobType })),
        match: (j, p) => p.includes(j.jobTypeId ?? NONE),
    },
    {
        key: "typeGroups", title: "Job Type Group", icon: "layer-group", section: "Job",
        options: jobs => distinct(jobs, j => ({ id: j.jobTypeGroupId, name: j.jobTypeGroup }), "No group"),
        match: (j, p) => p.includes(j.jobTypeGroupId || NONE),
    },
    {
        key: "sources", title: "Source", icon: "bullhorn", section: "Job",
        options: jobs => distinct(jobs, j => ({ id: j.jobSourceId, name: j.jobSource }), "No source"),
        match: (j, p) => p.includes(j.jobSourceId || NONE),
    },
    {
        key: "metros", title: "Metro", icon: "map", section: "Job",
        options: (jobs, ctx) => distinct(jobs, j => ({ id: j.metroId, name: j.metroName, color: ctx.metros.find(m => m.id == j.metroId)?.color }), "No metro"),
        match: (j, p) => p.includes(j.metroId || NONE),
    },

    // People
    {
        key: "techs", title: "Technician", icon: "user-helmet-safety", section: "People",
        options: jobs => distinct(jobs, j => ({ id: j.techId, name: j.tech?.fullName, person: true, avatarUrl: j.tech?.avatarUrl }), "No technician"),
        match: (j, p) => p.includes(j.techId || NONE),
    },
    {
        key: "techProgress", title: "Tech Progress", icon: "route", section: "People",
        options: (jobs, ctx) => states(jobs, ctx, TECH_PROGRESS),
        match: testOf(TECH_PROGRESS),
    },
    {
        key: "techCall", title: "Tech Called Customer", icon: "phone-circle", section: "People",
        options: (jobs, ctx) => states(jobs, ctx, TECH_CALL),
        match: testOf(TECH_CALL),
    },
    {
        key: "creators", title: "Created By", icon: "user-pen", section: "People",
        options: jobs => distinct(jobs, j => ({ id: j.creatorUserId, name: j.creatorUser?.fullName, person: true, avatarUrl: j.creatorUser?.avatarUrl })),
        match: (j, p) => p.includes(j.creatorUserId ?? NONE),
    },
    {
        key: "modifiers", title: "Last Updated By", icon: "user-clock", section: "People",
        options: jobs => distinct(jobs, j => ({ id: j.lastUpdateUserId ?? j.creatorUserId, name: (j.lastUpdateUser ?? j.creatorUser)?.fullName, person: true, avatarUrl: (j.lastUpdateUser ?? j.creatorUser)?.avatarUrl })),
        match: (j, p) => p.includes(j.lastUpdateUserId ?? j.creatorUserId ?? NONE),
    },

    // Labels
    {
        key: "flags", title: "Flags", icon: "flag", section: "Labels",
        options: jobs => distinctMany(jobs, j => j.flags, "No flags"),
        match: (j, p) => (j.flagIds?.length ?? 0) == 0 ? p.includes(NONE) : j.flagIds!.some(f => p.includes(f)),
    },
    {
        key: "tags", title: "Tags", icon: "tag", section: "Labels",
        options: jobs => distinctMany(jobs, j => j.tags, "No tags"),
        match: (j, p) => (j.tagIds?.length ?? 0) == 0 ? p.includes(NONE) : j.tagIds!.some(t => p.includes(t)),
    },
    {
        key: "colors", title: "Job Color", icon: "palette", section: "Labels",
        options: (jobs, ctx) => distinct(jobs, j => {
            if (!j.color) return undefined;
            const named = ctx.jobColors.find(c => c.color?.toLowerCase() == j.color!.toLowerCase());
            return { id: j.color.toLowerCase(), name: named?.name ?? j.color, color: j.color };
        }, "No color"),
        match: (j, p) => p.includes(j.color ? j.color.toLowerCase() : NONE),
    },

    // Activity
    {
        key: "activity", title: "Calls, Notes & Files", icon: "comments", section: "Activity",
        options: (jobs, ctx) => states(jobs, ctx, ACTIVITY),
        match: testOf(ACTIVITY),
    },
    {
        key: "dataQuality", title: "Job Info", icon: "clipboard-list", section: "Activity",
        options: (jobs, ctx) => states(jobs, ctx, DATA_QUALITY),
        match: testOf(DATA_QUALITY),
    },

    // Dates (each also takes a custom from/to range)
    {
        key: "appointment", title: "Appointment", icon: "calendar-clock", section: "Dates",
        options: (jobs, ctx) => [...states(jobs, ctx, APPOINTMENT), CUSTOM_OPTION],
        match: testOf(APPOINTMENT),
        dateOf: j => (j.filterTimes ?? withFilterTimes(j).filterTimes)?.appointment,
    },
    {
        key: "created", title: "Created", icon: "calendar-plus", section: "Dates",
        options: (jobs, ctx) => [...states(jobs, ctx, CREATED), CUSTOM_OPTION],
        match: testOf(CREATED),
        dateOf: j => (j.filterTimes ?? withFilterTimes(j).filterTimes)?.created,
    },
    {
        key: "modified", title: "Last Modified", icon: "calendar-pen", section: "Dates",
        options: (jobs, ctx) => [...states(jobs, ctx, MODIFIED), CUSTOM_OPTION],
        match: testOf(MODIFIED),
        dateOf: j => (j.filterTimes ?? withFilterTimes(j).filterTimes)?.modified,
    },
]

/** Adds the date filters' UTC numbers to a job from the server (mutates and returns it). */
export const withFilterTimes = (j: Job): Job => {
    j.filterTimes = {
        appointment: j.appointmentDate ? ms(j.appointmentFrom) : undefined,
        created: ms(j.createdAt),
        modified: ms(j.modifiedAt ?? j.createdAt),
    };
    return j;
}

export const JOB_FILTER_SECTIONS = Array.from(new Set(JOB_FILTERS.map(f => f.section)));

/** Reads every filter's picked ids (and the date filters' custom ranges) from a query string. */
export const readJobFilters = (search: string): JobFilterValues => {
    const params = new URLSearchParams(search);
    const res: JobFilterValues = {};
    JOB_FILTERS.forEach(f => {
        const v = (params.get(f.key) ?? "").split(",").filter(Boolean);
        if (v.length > 0) res[f.key] = v;
        const r = params.get(rangeKey(f.key));
        if (f.dateOf && r) res[rangeKey(f.key)] = [r];
    });
    return cleanJobFilters(res);
}

/** The values as the address holds them: one comma-joined string per filter (and range). */
export const jobFilterParams = (values: JobFilterValues): Record<string, string | undefined> => {
    const res: Record<string, string | undefined> = {};
    JOB_FILTERS.forEach(f => {
        res[f.key] = values[f.key]?.length ? values[f.key].join(",") : undefined;
        if (f.dateOf) res[rangeKey(f.key)] = values[rangeKey(f.key)]?.[0];
    });
    return res;
}

/** Drops empty filters, a "Custom range" pick with no range, and a range without its pick. */
export const cleanJobFilters = (values: JobFilterValues): JobFilterValues => {
    const res: JobFilterValues = {};
    JOB_FILTERS.forEach(f => {
        const range = f.dateOf ? readRange(values, f.key) : undefined;
        // Date filters are single-select: one period or the custom range.
        const picked = (values[f.key] ?? []).filter(id => id != CUSTOM || !!range).slice(0, f.dateOf ? 1 : undefined);
        if (picked.length == 0) return;
        res[f.key] = picked;
        if (range && picked.includes(CUSTOM)) res[rangeKey(f.key)] = writeRange(range)!;
    });
    return res;
}

/** How many filters are set (a picked "Custom range" without a range doesn't count). */
export const countJobFilters = (values: JobFilterValues) => Object.keys(cleanJobFilters(values)).filter(k => JOB_FILTERS.some(f => f.key == k)).length;

/** The predicate for a set of picked values, or undefined when nothing is picked. */
export const buildJobFilter = (values: JobFilterValues, ctx: JobFilterContext): ((job: Job) => boolean) | undefined => {
    const clean = cleanJobFilters(values);
    const active = JOB_FILTERS.filter(f => (clean[f.key]?.length ?? 0) > 0);
    if (active.length == 0) return undefined;
    return (job: Job) => active.every(f => {
        const picked = clean[f.key];
        if (f.match(job, picked, ctx)) return true;
        if (!f.dateOf || !picked.includes(CUSTOM)) return false;
        const range = readRange(clean, f.key)!;
        const t = f.dateOf(job);
        return t != null && (!range.from || t >= range.from) && (!range.to || t <= range.to);
    });
}

/** Every piece of a job the Find box looks through. */
const searchTextOf = (j: Job) => [
    j.jobId, j.fullName, j.company, j.emailAddress,
    j.phoneRaw, j.phone?.phone, j.phone?.phoneFriendly, j.phone?.extension,
    j.phoneContact?.phone, j.phoneContact?.phoneFriendly,
    j.address?.title, j.address?.address?.label,
    j.jobDetails, j.jobType, j.jobTypeWithGroup, j.jobTypeGroup,
    j.jobSource, j.jobSourceType, j.jobStatus?.name, j.metroName,
    j.tech?.fullName, j.creatorUser?.fullName, j.lastUpdateUser?.fullName, j.lastStatusUser?.fullName,
    j.appointmentDateText, j.lastStatusNote, j.issueText,
    ...(j.flags ?? []).map(f => f.name), ...(j.tags ?? []).map(t => t.name),
].filter(Boolean).join("\n").toLowerCase();

const digitsOf = (s?: string) => (s ?? "").replace(/\D/g, "");

/** The Find box's match: every word of the keyword must appear somewhere in the job (any field).
 *  A word with digits also matches phone numbers written with spaces, dashes or brackets. */
export const jobMatchesKeyword = (job: Job, keyword?: string) => {
    const words = (keyword ?? "").toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length == 0) return true;
    const text = searchTextOf(job);
    const phones = [job.phoneRaw, job.phone?.phone, job.phoneContact?.phone].map(digitsOf).join(" ");
    // "(203) 684" -> words "(203)" "684"; each phone-like word is compared as bare digits.
    const phoneLike = (w: string) => /^[\d()+\-.]+$/.test(w) && digitsOf(w).length >= 3;
    return words.every(w => text.includes(w) || (phoneLike(w) && phones.includes(digitsOf(w))));
}

